import { isMobilePhoneUserAgent } from './engine';
import { isWeChatWebView } from './wechat-media-unlock';

const isIosWebKit = (userAgent: string): boolean => /iPhone|iPad|iPod/i.test(userAgent);

export const RTC_AUDIO_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  autoGainControl: true,
  echoCancellation: true,
  noiseSuppression: true,
};

/**
 * H5 / 微信会把第一次 getUserMedia 的约束锁进采集会话。
 * 若第一次只开了摄像头，后面 SDK startAudioCapture 会采到静音轨。
 * 若把浏览器麦挂在预览 video 上，回声消除会把下行缓冲拉得很长。
 * /live-pro 的解法：先 getUserMedia({ audio: true }) 再整段停掉，摄像头只采画面，麦交给 SDK。
 * 桌面 Web 没有这把锁，保持这条正常链路即可。
 */
export const needsRtcMicrophoneWarmup = (
  userAgent = typeof navigator === 'undefined' ? '' : (navigator.userAgent ?? ''),
): boolean => isWeChatWebView(userAgent) || isIosWebKit(userAgent) || isMobilePhoneUserAgent(userAgent);

let hasWarmedUpRtcMicrophone = false;

export const resetRtcMicrophoneWarmupForTests = (): void => {
  hasWarmedUpRtcMicrophone = false;
};

export const warmupRtcMicrophoneIfNeeded = async (isCancelled?: () => boolean): Promise<void> => {
  if (hasWarmedUpRtcMicrophone || !needsRtcMicrophoneWarmup()) {
    return;
  }
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    return;
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => {
    if (track.readyState !== 'ended') {
      track.stop();
    }
  });
  if (isCancelled?.()) {
    return;
  }
  hasWarmedUpRtcMicrophone = true;
};

export interface RtcMicrophonePermissionResult {
  /** 授权时实际拿到的输入设备。必须回传给 startAudioCapture，否则 SDK 会另选系统默认设备。 */
  deviceId?: string;
  granted: boolean;
}

/**
 * editing-pro 在申请摄像头前单独请求麦克风权限。临时轨不参与 RTC 推流，
 * 只用于把权限交互与后续画面采集拆开；拒绝麦克风不阻断摄像头和无麦直播。
 *
 * 必须把授权时的 deviceId 一起带回去：多麦克风设备上「系统默认输入」未必是这里授权到的那一只，
 * SDK 可能采到副麦（降噪参考麦，本身就是拿来收环境声的）或蓝牙 HFP 低质量麦，听感就是明显变嘈杂。
 * 对齐 /live-pro 的 requestMicrophone。
 */
export const requestRtcMicrophonePermission = async (): Promise<RtcMicrophonePermissionResult> => {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    return { granted: false };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: RTC_AUDIO_CAPTURE_CONSTRAINTS });
    // 取 deviceId 必须在 stop 之前：轨道停掉后 getSettings 不再保证返回设备信息。
    const deviceId = stream.getAudioTracks()[0]?.getSettings().deviceId?.trim() || undefined;
    stream.getTracks().forEach((track) => {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    });
    hasWarmedUpRtcMicrophone = true;
    return { deviceId, granted: true };
  } catch {
    return { granted: false };
  }
};

export const openRtcCameraStream = async (
  video: MediaStreamConstraints['video'],
  isCancelled?: () => boolean,
  options?: { skipWarmup?: boolean },
): Promise<MediaStream | null> => {
  if (!options?.skipWarmup && needsRtcMicrophoneWarmup() && !hasWarmedUpRtcMicrophone) {
    await warmupRtcMicrophoneIfNeeded(isCancelled);
    if (isCancelled?.()) {
      return null;
    }
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video });
  if (isCancelled?.()) {
    stream.getTracks().forEach((track) => {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    });
    return null;
  }
  return stream;
};
